import React, { useEffect, useRef, useState } from 'react';
import { Typography, Switch, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import dashboardPreferencesService from "../../api/tenantApi";
import { getCurrentUserId } from "../../authUtils";
import { toast } from 'react-toastify';
import { DLOCAL_NOTIFICATION_TENANT_ID, saveNotificationSetting } from '../../utils/notificationPreferenceSettings';



// Custom styled Switch component
const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 45,
  height: 25,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#5186EC',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#5186EC',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 21,
    height: 21,
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9E9',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const NotificationSettings = ({
  isExpanded,
  notificationPreferences,
  preferenceStatus = {},
  onUpdatePreferences,
  onPreferenceStatusChange,
  tenantId
}) => {
  const [saving, setSaving] = useState(false);
  const saveInFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const handlePreferenceChange = async (type, channel) => {
    if (saveInFlight.current || !['ready', 'configuring'].includes(preferenceStatus[channel])) return;
    saveInFlight.current = true;
    setSaving(true);
    try {
      const preferences = await saveNotificationSetting(dashboardPreferencesService, {
        userId: getCurrentUserId(),
        tenantId,
        channel,
        category: type,
        enabled: notificationPreferences[channel]?.[type] !== true,
      });
      if (!mounted.current) return;
      onUpdatePreferences({ ...notificationPreferences, [channel]: preferences });
      onPreferenceStatusChange(channel, 'ready');
      toast.success('Notification preference saved');
    } catch (error) {
      if (!mounted.current) return;
      // A failed readback can follow a successful write. Keep that uncertainty
      // visible instead of showing the old switch as a verified saved choice.
      onPreferenceStatusChange(channel, 'error');
      toast.error('Could not verify notification preferences. Reload to try again.');
    } finally {
      saveInFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  if (!isExpanded) return null;

  const notificationTypes = [
    {
      key: 'Complete',
      title: 'Notes',
      description: 'Notes are notifications that inform users on completed process'
    },
    {
      key: 'Update',
      title: 'Updates',
      description: 'Update notifications include information about new events or other type of information recently added to a company card.'
    },
    {
      key: 'Status Change',
      title: 'Status Changes',
      description: "This type of notification will alert you to any significant changes in the company's scores or credit recommendations."
    },
    {
      key: 'Financial Report',
      title: 'Financial Reports',
      description: 'This type of notification will alert you to new Financial Reports added in the company.'
    },
    {
      key: 'Critical Risk Alert',
      title: 'Critical Risk Alerts',
      description: 'Critical alerts for significant risk changes including recommendation drops (>35%).'
    },
    {
      channels: ['weekly'],
      key: 'AR Summary',
      title: 'Weekly AR Risk Summary',
      badge: 'NEW',
      description: 'A weekly email summarizing your accounts-receivable risk — customers at risk (ARC ≥ 8), exposure, and risk distribution. Choose delivery in the "Weekly News" column.'
    }
  ];

  if (tenantId === DLOCAL_NOTIFICATION_TENANT_ID) {
    notificationTypes.push(
      { key: 'Error', title: 'Processing Errors', description: 'Errors that prevent a company-data update or requested process from completing.' },
      { key: 'Scheduled Portfolio Report', title: 'Scheduled Portfolio Reports', channels: ['email'], description: 'Scheduled exports of your portfolio data.' },
      { key: 'Rescore Summary', title: 'Rescore Summaries', channels: ['email'], description: 'Completion summaries for rescore jobs you requested.' },
      { key: 'Newsletter', title: 'Newsletters', channels: ['email'], description: 'Newsletters and administrative informational emails.' },
    );
  }

  const channels = [
    {
      key: 'inApp',
      title: 'In-App',
      tooltip: "You'll get an update in the notification bar on the right-hand side of the screen."
    },
    {
      key: 'weekly',
      title: 'Weekly News',
      tooltip: 'We will send you a weekly email with all the relevant updates that happen during the week.'
    },
    {
      key: 'email',
      title: 'Email',
      tooltip: 'We will send you immediate emails. This is recommended only for status updates.'
    },
    {
      key: 'slack',
      title: 'Slack',
      tooltip: 'We will send you immediate s in slack.'
    }
  ];

  return (
    <div className="notification-settings" style={{
      background: '#FAFAFC',
      padding: '20px',
      borderRadius: '0 0 8px 8px',
      boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 1px 3px rgba(0, 0, 0, 0.08)',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ width: '30%' }}></th>
            {channels.map(channel => (
              <th key={channel.key} style={{ padding: '10px', textAlign: 'center' }}>
                {channel.title}
                <Tooltip title={channel.tooltip}>
                  <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: '5px' }} />
                </Tooltip>
                {preferenceStatus[channel.key] === 'error' && (
                  <Typography role="status" variant="caption" display="block">Unavailable — reload to try again</Typography>
                )}
                {preferenceStatus[channel.key] === 'missing' && (
                  <div>
                    <Typography variant="caption" display="block">No saved preferences</Typography>
                    <button type="button" disabled={saving} onClick={() => onPreferenceStatusChange(channel.key, 'configuring')}>
                      Set {channel.title} preferences
                    </button>
                  </div>
                )}
                {preferenceStatus[channel.key] === 'configuring' && (
                  <Typography variant="caption" display="block">Choose a category to save</Typography>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {notificationTypes.map(type => (
            <tr key={type.key}>
              <td style={{ padding: '10px' }}>
                <Typography variant="subtitle1" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {type.title}
                  {type.badge && (
                    <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.04em', color: '#0E2A5A', background: '#FFD34E', borderRadius: 5, padding: '1px 6px', lineHeight: 1.6 }}>
                      {type.badge}
                    </span>
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {type.description}
                </Typography>
              </td>
              {channels.map(channel => {
                const weeklyUnavailable = tenantId === DLOCAL_NOTIFICATION_TENANT_ID && channel.key === 'weekly' && type.key !== 'AR Summary';
                if (weeklyUnavailable || (type.channels && !type.channels.includes(channel.key))) {
                  return <td key={channel.key} style={{ textAlign: 'center' }} aria-label="Not available for this channel">—</td>;
                }
                const available = ['ready', 'configuring'].includes(preferenceStatus[channel.key]);
                const isChecked = available && notificationPreferences[channel.key]?.[type.key] === true;
                return (
                  <td key={channel.key} style={{ textAlign: 'center' }}>
                    <CustomSwitch
                      checked={isChecked}
                      disabled={!available || saving}
                      inputProps={{ 'aria-label': `${type.title} via ${channel.title}` }}
                      onChange={() => handlePreferenceChange(type.key, channel.key)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default NotificationSettings;
